import {computed, inject, Injectable} from '@angular/core';
import {DirectusService, getDirectusTranslation} from '@frontend/directus';
import {
  AuditInfo,
  ProjectOfficeResponse,
  SystemRecordingToolControllerService,
  SystemRecordingsControllerService,
  SystemRecordingToolsUpsertRequest,
  ProviderWithToolsRequest,
  SystemRecordingResponse,
  ProviderResponse,
} from '@frontend/shared-api';
import {FunctionEntry, ModuleData, ProviderData} from '../system-recording.types';
import {firstValueFrom} from 'rxjs';
import {map} from 'rxjs/operators';
import {FixedValueService} from '../../../../../../../shared/services/fixed-value.service';
import {TranslocoService} from '@jsverse/transloco';
import {HrToolsMiniProviderService} from './hr-tools-mini-provider.service';

@Injectable({providedIn: 'root'})
export class LoadOrSyncSystemRecordingDataService {
  private readonly systemRecordingToolApi = inject(SystemRecordingToolControllerService);
  private readonly systemRecordingsApi = inject(SystemRecordingsControllerService);
  private readonly directusService = inject(DirectusService);
  private readonly fixedValueService = inject(FixedValueService);
  private readonly translocoService = inject(TranslocoService);
  private readonly hrToolsMiniProviderService = inject(HrToolsMiniProviderService);

  protected readonly currentLang = computed(() => this.translocoService.getActiveLang());

  async loadProviderAndToolsFromBackend(projectId: number): Promise<ProviderData[]> {
    const toolsResponse = await firstValueFrom(
      this.systemRecordingToolApi.getSystemRecordingTools({projectId}),
    );

    return toolsResponse.providers.map((provider: ProviderResponse) => ({
      providerId: provider.providerId,
      providerName: provider.providerName,
      tools: provider.tools.map((tool) => ({
        toolId: tool.toolId,
        toolName: tool.toolName,
        directusId: tool.directusId,
      })),
    }));
  }

  async seedProviderAndToolsAtBackend(
    projectId: number,
    hrToolsFromDirectus: ProviderData[],
  ): Promise<void> {
    if (hrToolsFromDirectus.length > 0) {
      const providersRequest: ProviderWithToolsRequest[] = hrToolsFromDirectus.map((provider) => ({
        providerId: undefined,
        providerName: provider.providerName,
        tools: provider.tools.map((tool) => ({
          toolId: undefined,
          toolName: tool.toolName,
          directusId: tool.directusId,
        })),
      }));

      const upsertRequest: SystemRecordingToolsUpsertRequest = {
        providers: providersRequest,
      };

      await firstValueFrom(
        this.systemRecordingToolApi.upsertSystemRecordingTools({
          projectId,
          systemRecordingToolsUpsertRequest: upsertRequest,
        }),
      );
    }
  }

  async loadHrToolsFromDirectus(projectId: number): Promise<ProviderData[]> {
    const hrToolsResponse = await this.getHrToolsFixedValuesWithCollectionId(projectId);
    const directusHrTools = await this.hrToolsMiniProviderService.getHrTools();

    const fixedValueIds = new Set(hrToolsResponse.directusIds);
    const filteredHrTools = directusHrTools.filter((tool) => fixedValueIds.has(tool.id));

    const providerMap = new Map<string, ProviderData>();

    filteredHrTools.forEach((tool) => {
      const providerId = tool.toolProvider.id;
      if (!providerMap.has(providerId)) {
        providerMap.set(providerId, {
          providerId: 0,
          providerName: tool.toolProvider.name,
          tools: [],
        });
      }
      providerMap.get(providerId)!.tools.push({
        toolId: 0,
        toolName: getDirectusTranslation(tool.translations, this.currentLang(), tool.name),
        directusId: tool.id,
      });
    });

    return Array.from(providerMap.values());
  }

  private async getHrToolsFixedValuesWithCollectionId(
    projectId: number,
  ): Promise<{collectionId: number; directusIds: string[]}> {
    return await firstValueFrom(
      this.fixedValueService.loadOrInitializeFixedValuesWithCollectionId(
        projectId,
        'hr_tools',
        () => this.directusService.loadHrToolIds(),
      ),
    );
  }

  async loadFixedModulesAndFunctions(
    projectId: number,
    offices: ProjectOfficeResponse[],
  ): Promise<{modules: ModuleData[]; auditInfo: AuditInfo | null}> {
    const projectFunctionIds = new Set(
      offices
        .flatMap((office) => Array.from(office.functions))
        .map((func) => Number(func.directusId))
        .filter((id) => !isNaN(id)),
    );

    const [fixedValues, modulesResponse, systemRecordings] = await Promise.all([
      this.getModuleAndModuleFunctionFixedValues(projectId),
      firstValueFrom(this.directusService.loadModuleFunctionsGroupedByModule()),
      firstValueFrom(this.systemRecordingsApi.getAllSystemRecording({projectId})),
    ]);

    const systemRecordingsByFunctionId = this.groupSystemRecordingsByFunctionId(
      systemRecordings.entries,
    );

    const fixedModuleIds = fixedValues['module'] || [];
    const fixedFunctionIds = fixedValues['module_function'] || [];
    const fixedModuleSet = new Set(fixedModuleIds);
    const fixedFunctionSet = new Set(fixedFunctionIds);

    const result: ModuleData[] = [];

    modulesResponse.forEach((module, moduleId) => {
      if (fixedModuleSet.size > 0 && !fixedModuleSet.has(String(moduleId))) {
        return;
      }

      const filteredFunctions =
        fixedFunctionSet.size > 0
          ? module.functions.filter((func) => fixedFunctionSet.has(String(func.id)))
          : module.functions;

      if (filteredFunctions.length === 0) {
        return;
      }

      const moduleFunctions: FunctionEntry[] = [];

      const currentLang = this.currentLang();
      const moduleTranslatedName = getDirectusTranslation(
        module.translations,
        currentLang,
        module.name,
      );

      filteredFunctions.forEach((func) => {
        const funcTranslatedName = getDirectusTranslation(
          func.translations,
          currentLang,
          func.name,
        );
        const recordings = systemRecordingsByFunctionId.get(String(func.id)) ?? [null];
        recordings.forEach((rec) => {
          moduleFunctions.push({
            directusId: func.id,
            uniqueId: crypto.randomUUID(),
            backendId: rec?.id ?? null,
            name: func.name,
            translatedName: funcTranslatedName,
            translations: func.translations,
            isInProjectScope: projectFunctionIds.has(func.id),
            selectedOffices: rec
              ? offices.filter((office) => rec.officeIds.includes(office.officeId))
              : [],
            plannedAction: rec?.plannedAction ?? null,
            toolId: rec?.toolId ?? null,
            providerId: rec?.providerId ?? null,
          });
        });
      });

      result.push({
        id: module.id,
        name: module.name,
        translatedName: moduleTranslatedName,
        translations: module.translations,
        functions: moduleFunctions,
        hasInScopeFunctions: moduleFunctions.some((func) => func.isInProjectScope),
        completedFunctions: 0,
        totalFunctions: moduleFunctions.length,
      });
    });

    return {modules: result, auditInfo: systemRecordings.auditInfo ?? null};
  }

  private getModuleAndModuleFunctionFixedValues(projectId: number) {
    return firstValueFrom(
      this.fixedValueService.loadOrInitializeFixedValuesBatch(projectId, [
        {
          collectionName: 'module',
          loadFunction: () =>
            this.directusService
              .loadModules()
              .pipe(map((modules) => modules.map((m) => ({...m, id: String(m.id)})))),
        },
        {
          collectionName: 'module_function',
          loadFunction: () =>
            this.directusService
              .loadModuleFunctions()
              .pipe(map((functions) => functions.map((f) => ({...f, id: String(f.id)})))),
        },
      ]),
    );
  }

  private groupSystemRecordingsByFunctionId(
    entries: SystemRecordingResponse[],
  ): Map<string, SystemRecordingResponse[]> {
    const entriesByFunctionId = new Map<string, SystemRecordingResponse[]>();

    entries.forEach((entry) => {
      if (!entriesByFunctionId.has(entry.functionDirectusId)) {
        entriesByFunctionId.set(entry.functionDirectusId, []);
      }
      entriesByFunctionId.get(entry.functionDirectusId)!.push(entry);
    });

    return entriesByFunctionId;
  }
}
